import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import type { MediaItem, ProcessingStatus } from '../types';
import { formatBytes, formatFullDate } from '../lib/format';

const FILTERS: Array<{ key: ProcessingStatus | 'ALL'; label: string }> = [
  { key: 'ALL', label: 'All' },
  { key: 'READY', label: 'Ready' },
  { key: 'PENDING', label: 'Processing' },
  { key: 'FAILED', label: 'Failed' },
];

const pill = 'focus-ring rounded-full px-4 py-1.5 text-sm font-medium transition';

/**
 * Raw media browser. This is deliberately the "admin" surface: it exists so
 * processing failures and metadata quality are inspectable, and it is where
 * memories are deleted (one from its tile, or several with Select). The
 * primary way to use the product remains Home -> Season -> Episode.
 */
export default function LibraryPage() {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [filter, setFilter] = useState<ProcessingStatus | 'ALL'>('ALL');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Ids waiting for the "Delete?" confirmation. */
  const [confirming, setConfirming] = useState<string[] | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const stopSelecting = () => {
    setSelecting(false);
    setSelected(new Set());
  };
  const afterDelete = useCallback(() => {
    setConfirming(null);
    setSelecting(false);
    setSelected(new Set());
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api
        .media({ status: filter === 'ALL' ? undefined : filter, limit: 200 })
        .then((res) => {
          if (cancelled) return;
          setItems(res.items);
          setCounts(res.counts as Record<string, number>);
          setError(null);
        })
        .catch((err) => !cancelled && setError((err as Error).message))
        .finally(() => !cancelled && setLoading(false));
    };
    load();
    // Poll while anything is still being processed so the grid fills in live.
    const timer = setInterval(() => {
      api
        .mediaStats()
        .then((s) => {
          if (!cancelled && s.busy) load();
        })
        .catch(() => undefined);
    }, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [filter, reloadKey]);

  return (
    <div className="mx-auto max-w-[1600px] px-4 py-10 sm:px-6 lg:px-10">
      <h1 className="mb-2 text-3xl font-extrabold tracking-tight sm:text-4xl">Library</h1>
      <p className="mb-6 text-sm text-white/55">
        Every file Memoryflix knows about, with the metadata it extracted.
      </p>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`${pill} ${filter === f.key ? 'bg-white text-black' : 'bg-white/8 text-white/65 hover:bg-white/15'}`}
          >
            {f.label}
            {f.key !== 'ALL' && counts[f.key] ? ` (${counts[f.key]})` : ''}
          </button>
        ))}
        <span className="flex-1" />
        {selecting ? (
          <>
            <span className="text-sm text-white/60" aria-live="polite">
              {selected.size === 0 ? 'Tap photos to select' : `${selected.size} selected`}
            </span>
            <button
              onClick={() => setConfirming([...selected])}
              disabled={selected.size === 0}
              className={`${pill} bg-red-600 font-bold text-white hover:bg-red-500 disabled:opacity-40`}
            >
              Delete
            </button>
            <button onClick={stopSelecting} className={`${pill} bg-white/8 text-white/75 hover:bg-white/15`}>
              Cancel
            </button>
          </>
        ) : (
          items.length > 0 && (
            <button onClick={() => setSelecting(true)} className={`${pill} bg-white/8 text-white/75 hover:bg-white/15`}>
              Select
            </button>
          )
        )}
      </div>

      {error && <p className="text-sm text-red-300">{error}</p>}

      {loading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          {Array.from({ length: 16 }).map((_, i) => (
            <div key={i} className="skeleton aspect-square" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="py-20 text-center text-white/40">Nothing here yet.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          {items.map((item) => {
            const isSelected = selected.has(item.id);
            return (
              <figure
                key={item.id}
                className={`group relative overflow-hidden rounded-lg bg-ink-850 ${
                  isSelected ? 'ring-2 ring-accent' : 'ring-1 ring-white/5'
                }`}
                title={item.originalFilename}
              >
                <div className="aspect-square">
                  {item.thumbnailUrl ? (
                    <img
                      src={item.thumbnailUrl}
                      alt={item.originalFilename}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  ) : item.type === 'VIDEO' ? (
                    <video src={item.url} preload="metadata" muted playsInline className="h-full w-full object-cover" />
                  ) : (
                    <img src={item.url} alt={item.originalFilename} loading="lazy" className="h-full w-full object-cover" />
                  )}
                </div>

                {/* Status chip */}
                {item.processingStatus !== 'READY' && (
                  <span
                    className={`absolute top-1 left-1 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                      item.processingStatus === 'FAILED'
                        ? 'bg-red-600 text-white'
                        : 'bg-amber-500/90 text-black'
                    }`}
                  >
                    {item.processingStatus}
                  </span>
                )}

                {item.type === 'VIDEO' && (
                  <span className="absolute bottom-1 left-1 rounded bg-black/70 p-1 group-hover:opacity-0">
                    <svg viewBox="0 0 24 24" className="h-3 w-3 text-white" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                )}

                {/* Hover detail */}
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
                  <p className="truncate text-[10px] text-white/80">{item.originalFilename}</p>
                  <p className="text-[9px] text-white/50">
                    {formatFullDate(item.capturedAt)}
                  </p>
                  <p className="text-[9px] text-white/35">
                    via {item.capturedAtSource.replace(/_/g, ' ').toLowerCase()} · {formatBytes(item.fileSize)}
                  </p>
                  {item.locationName && <p className="truncate text-[9px] text-white/35">📍 {item.locationName}</p>}
                  {item.processingError && (
                    <p className="mt-0.5 line-clamp-2 text-[9px] text-red-300">{item.processingError}</p>
                  )}
                </figcaption>

                {selecting ? (
                  <button
                    onClick={() => toggle(item.id)}
                    aria-pressed={isSelected}
                    aria-label={`${isSelected ? 'Unselect' : 'Select'} ${item.originalFilename}`}
                    className="focus-ring absolute inset-0 flex items-start justify-end p-1.5"
                  >
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full ring-2 ${
                        isSelected ? 'bg-accent ring-accent' : 'bg-black/40 ring-white/80'
                      }`}
                    >
                      {isSelected && (
                        <svg viewBox="0 0 24 24" className="h-4 w-4 text-white" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
                          <path d="M5 12l5 5 9-10" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => setConfirming([item.id])}
                    aria-label={`Delete ${item.originalFilename}`}
                    title="Delete"
                    className="focus-ring absolute top-1 right-1 rounded-full bg-black/70 p-1.5 text-white/80 opacity-0 transition group-hover:opacity-100 hover:text-red-300 focus-visible:opacity-100 [@media(hover:none)]:hidden"
                  >
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </figure>
            );
          })}
        </div>
      )}

      {confirming && <DeleteDialog ids={confirming} onCancel={() => setConfirming(null)} onDeleted={afterDelete} />}
    </div>
  );
}

/** "Delete N memories?" — says plainly what goes and what stays. */
function DeleteDialog({ ids, onCancel, onDeleted }: { ids: string[]; onCancel: () => void; onDeleted: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    ref.current?.showModal();
  }, []);
  const n = ids.length;

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteMedia(ids);
      onDeleted();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <dialog
      ref={ref}
      aria-labelledby="delete-title"
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) onCancel();
      }}
      className="m-auto w-[min(440px,calc(100%-2rem))] rounded-2xl bg-ink-900 p-6 text-white ring-1 ring-white/10 backdrop:bg-black/75 backdrop:backdrop-blur-sm"
    >
      <h2 id="delete-title" className="mb-2 text-xl font-bold tracking-tight">
        {n === 1 ? 'Delete this memory?' : `Delete these ${n} memories?`}
      </h2>
      <p className="mb-2 text-sm text-white/70">
        {n === 1 ? 'It' : 'They'} will be removed from Memoryflix, including from episodes and stories. Episodes left
        empty disappear.
      </p>
      <p className="mb-5 text-sm text-white/50">
        The original on your phone, computer or Google Drive is not touched. This can’t be undone.
      </p>
      {error && (
        <p role="alert" className="mb-3 text-sm text-red-300">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={busy}
          autoFocus
          className="focus-ring rounded-md bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/15 disabled:opacity-40"
        >
          Cancel
        </button>
        <button
          onClick={() => void run()}
          disabled={busy}
          className="focus-ring rounded-md bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-500 disabled:opacity-50"
        >
          {busy ? 'Deleting…' : n === 1 ? 'Delete' : `Delete ${n}`}
        </button>
      </div>
    </dialog>
  );
}
